import EditIcon from '@mui/icons-material/Edit';
import { Alert, IconButton, Stack, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader } from '../../../components/Loader/Loader';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { ICaptableDataModel } from '../../../data-models/captable2.data-model';
import {
  clientInvestmentsStateJ,
  currentStepCapTableStateJ,
  otherInvestmentsStateJ,
  sortedShareClassesStateJ,
} from '../../../services/state/CapTableFormStateJ';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { FMT } from '../../../util/formatter-service';
import { getUnissuedOptionsWarnings } from './unissued-options-utils';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import { SubSection } from '../Summary/components/SubSection';
import { useProfileBasePath } from '../useProfileBasePath';
import { ShareClassList } from './AddShareClasses';
import { usePreviewCaptable } from './CapTableActions';
import { useCancelButtonProps } from './CapTableForm';
import { FormCard } from './commonStyledComponents';
import { CaptableSummaryGrid } from './SummaryGrid/CaptableSummaryGrid';
import { useCreateOrUpdateCaptable } from './useCreateOrUpdateCaptable';

const listStyle = {
  maxHeight: '30rem',
  overflow: 'auto',
  background: `
    linear-gradient(
      white 30%,
      rgba(255, 255, 255, 0)
    ) center top,
    
    linear-gradient(
      rgba(255, 255, 255, 0), 
      white 70%
    ) center bottom,
    
    radial-gradient(
      farthest-side at 50% 0,
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0)
    ) center top,
    
    radial-gradient(
      farthest-side at 50% 100%,
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0)
    ) center bottom
  `,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
  backgroundAttachment: 'local, local, scroll, scroll',
};

export function ReviewAndUpload() {
  const shareClasses = useAtomValue(sortedShareClassesStateJ);
  const navigate = useNavigate();
  const { captablePayload, createOrUpdateCaptable } = useCreateOrUpdateCaptable();
  const cancelButtonProps = useCancelButtonProps();
  const profilesBasePath = useProfileBasePath();

  const onUpload = useCallback(async () => {
    const data = await createOrUpdateCaptable(() => {
      navigate(`${profilesBasePath}/${ROUTES.COMPANY_PROFILE_CAP_TABLE}`);
    });
    if (data) {
      return Promise.resolve(true);
    }

    return Promise.resolve(false);
  }, [createOrUpdateCaptable, navigate, profilesBasePath]);

  return (
    <>
      <FormCard>
        <Typography variant='subtitle1'>{'Review and upload your cap table.'}</Typography>

        <UnissuedOptionsWarning />

        <ReviewSubSection step={0} title='Investments'>
          <ShareClassList shareClasses={shareClasses} style={listStyle} />
        </ReviewSubSection>

        <PreviewGrid payload={captablePayload} />
      </FormCard>

      <div>
        <StepperFormButtons
          stepState={currentStepCapTableStateJ}
          stepIsValid={onUpload}
          nextButtonLabel='Save'
          secondaryActionProps={cancelButtonProps}
        />
      </div>
    </>
  );
}

function UnissuedOptionsWarning() {
  const { isBetaFeature } = useFlags<MaggieFeatureFlags>();
  const shareClasses = useAtomValue(sortedShareClassesStateJ);
  const clientInvestments = useAtomValue(clientInvestmentsStateJ);
  const otherInvestments = useAtomValue(otherInvestmentsStateJ);

  const warnings = useMemo(
    () => getUnissuedOptionsWarnings(shareClasses, [...clientInvestments, ...otherInvestments]),
    [shareClasses, clientInvestments, otherInvestments]
  );

  if (!isBetaFeature || warnings.length === 0) {
    return null;
  }

  return (
    <Alert severity='warning' sx={{ my: '0.5rem' }}>
      <Typography variant='body2'>
        {
          'Unissued and granted options are added together for the total pool. If the unissued number already includes the granted options, edit "Number of Unissued Options" on the share class in the Investments step.'
        }
      </Typography>
      {warnings.map(({ name, unissued, granted }) => (
        <Typography key={name} variant='body2'>
          {`${name}: ${FMT.format('integer', unissued)} unissued + ${FMT.format('integer', granted)} granted = ${FMT.format('integer', unissued + granted)} total options.`}
        </Typography>
      ))}
    </Alert>
  );
}

function PreviewGrid({ payload }: { payload: Partial<ICaptableDataModel> }) {
  const previewCaptableHandler = usePreviewCaptable();
  const [preview, setPreview] = useState<ICaptableDataModel | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    previewCaptableHandler(payload).then((data) => {
      if (data) {
        setPreview(data);
      }
      setLoading(false);
    });
  }, [payload, previewCaptableHandler]);

  return (
    <ReviewSubSection step={1} title='Investment Details'>
      <Stack minHeight={'30rem'} height={'30rem'} maxHeight={'30rem'} overflow={'hidden'}>
        {loading && <Loader />}
        {preview && <CaptableSummaryGrid data={preview} />}
      </Stack>
    </ReviewSubSection>
  );
}

interface IReviewSubSectionProps extends PropsWithChildren {
  step: number;
  title: string;
}
export function ReviewSubSection({ step, title, children }: IReviewSubSectionProps) {
  const setCurrentStep = useSetAtom(currentStepCapTableStateJ);

  return (
    <SubSection
      style={{ margin: 0, padding: 0 }}
      title={title}
      noCard
      actionComponent={
        <IconButton onClick={() => setCurrentStep(step)}>
          <EditIcon fontSize='small' color='secondary' />
        </IconButton>
      }
      childrenContainerStyle={{ maxHeight: '30rem', marginBottom: '1rem' }}
    >
      {children}
    </SubSection>
  );
}
